import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { PlusIcon, XIcon } from "lucide-react";

import { useUpdateIntegration, type Integration } from "@/api/integrations";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface Props {
  integration: Integration | null;
  onClose: () => void;
}

export function EditIntegrationDialog({ integration, onClose }: Props) {
  const { t } = useTranslation();
  const update = useUpdateIntegration();
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [params, setParams] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState("");

  useEffect(() => {
    if (!integration) return;
    setName(integration.name);
    setEnabled(integration.enabled);
    // ponytail: params values are flat strings in practice; nested JSON would flatten on save
    setParams((integration.params ?? {}) as Record<string, string>);
    setNewKey("");
  }, [integration]);

  const setParam = (key: string, value: string) =>
    setParams({ ...params, [key]: value });

  const removeParam = (key: string) => {
    const next = { ...params };
    delete next[key];
    setParams(next);
  };

  const addParam = () => {
    if (!newKey.trim()) return;
    setParams({ ...params, [newKey.trim()]: "" });
    setNewKey("");
  };

  return (
    <Dialog
      open={integration != null}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.editTitle")}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="integration-enabled">
                {t("settings.integrations.common.enabled.label")}
              </Label>
              <p className="text-muted-foreground text-sm">
                {t("settings.integrations.common.enabled.description")}
              </p>
            </div>
            <Switch
              id="integration-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-name">
              {t("settings.integrations.common.name.label")}
            </Label>
            <Input
              id="integration-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t("settings.integrations.common.parameters.label")}</Label>
            <div className="divide-y rounded-md border">
              {Object.keys(params).length === 0 && (
                <p className="text-muted-foreground p-3 text-center text-sm">
                  {t("settings.integrations.common.noParams")}
                </p>
              )}
              {Object.entries(params).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 p-2">
                  <span className="w-40 shrink-0 truncate text-sm font-medium">
                    {key}
                  </span>
                  <Input
                    aria-label={key}
                    value={value}
                    onChange={(e) => setParam(key, e.target.value)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.buttons.delete")}
                    onClick={() => removeParam(key)}
                  >
                    <XIcon />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={newKey}
                placeholder={t(
                  "settings.integrations.propertyKeyDialog.placeholder",
                )}
                onChange={(e) => setNewKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addParam()}
              />
              <Button variant="outline" onClick={addParam}>
                <PlusIcon />
                {t("settings.integrations.common.parameters.add")}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.buttons.cancel")}
          </Button>
          <Button
            disabled={update.isPending}
            onClick={() =>
              integration &&
              update.mutate(
                { id: integration.id, name, enabled, params },
                { onSuccess: onClose },
              )
            }
          >
            {t("common.buttons.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
